import { createAttioPerson } from '@/helpers/attio/attio.server';
import { authMiddleware } from '@/helpers/authMiddleware';
import { prismaClient } from '@/helpers/prismaClient.server';
import { getPlunkCredentials } from '@/helpers/plunkClient.server';
import { requireOrganizerAccess } from '@/helpers/requireAuth.server';
import { createServerFn } from '@tanstack/react-start';
import { z } from 'zod';

const SITE_URL = process.env.VITE_SITE_URL || 'http://localhost:3000';
const INVITE_EXPIRY_DAYS = 14;

const InviteToOrganizerInput = z.object({
  organizerId: z.string().uuid(),
  email: z.string().email('Please enter a valid email address'),
});

const RevokeInviteInput = z.object({
  inviteId: z.string().uuid(),
  organizerId: z.string().uuid(),
});

const RemoveAdminInput = z.object({
  adminId: z.string().uuid(),
  organizerId: z.string().uuid(),
});

const ResendInviteInput = z.object({
  inviteId: z.string().uuid(),
  organizerId: z.string().uuid(),
});

/**
 * Sends an invite email to a user to join an organization.
 * Creates an invite record with a unique token and 14-day expiry,
 * then triggers a Plunk email template via event tracking.
 */
export const inviteToOrganizer = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof InviteToOrganizerInput> => {
      const result = InviteToOrganizerInput.safeParse(data);
      if (!result.success) {
        throw new Error(
          result.error.errors.map((e) => e.message).join(', ')
        );
      }
      return result.data;
    }
  )
  .handler(async ({ data, context }) => {
    const { profile } = await requireOrganizerAccess(
      data.organizerId,
      context.auth,
    );
    const db = await prismaClient();

    const organizer = await db.organizer.findUnique({
      where: { id: data.organizerId },
      select: { id: true, name: true, logoUrl: true },
    });

    if (!organizer) {
      throw new Error('Organization not found');
    }

    // Check if user is already an admin
    const existingAdmin = await db.organizerAdmin.findFirst({
      where: {
        organizerId: data.organizerId,
        profile: { email: data.email },
      },
    });

    if (existingAdmin) {
      throw new Error('This user is already an admin of the organization');
    }

    // Check for existing pending invite
    const existingInvite = await db.organizerInvite.findUnique({
      where: {
        email_organizerId: {
          email: data.email,
          organizerId: data.organizerId,
        },
      },
    });

    if (existingInvite && existingInvite.status === 'pending') {
      const isExpired = new Date() > new Date(existingInvite.expiresAt);
      if (!isExpired) {
        throw new Error(
          'An invite has already been sent to this email address'
        );
      }
    }

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + INVITE_EXPIRY_DAYS);

    // Delete any existing invite for this email+org and create fresh
    const invite = await db.$transaction(async (tx) => {
      if (existingInvite) {
        await tx.organizerInvite.delete({
          where: { id: existingInvite.id },
        });
      }

      return tx.organizerInvite.create({
        data: {
          email: data.email,
          organizerId: data.organizerId,
          invitedById: profile.id,
          expiresAt,
        },
      });
    });

    await sendInviteEmail({
      email: data.email,
      token: invite.token,
      organizerName: organizer.name,
      organizerLogoUrl: organizer.logoUrl,
    });

    return { success: true };
  });

/**
 * Resends an expired or pending invite by generating a new token
 * and extending the expiry date.
 */
export const resendInvite = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof ResendInviteInput> => {
      const result = ResendInviteInput.safeParse(data);
      if (!result.success) {
        throw new Error('Invalid input');
      }
      return result.data;
    }
  )
  .handler(async ({ data, context }) => {
    const { profile } = await requireOrganizerAccess(
      data.organizerId,
      context.auth,
    );
    const db = await prismaClient();

    const invite = await db.organizerInvite.findFirst({
      where: {
        id: data.inviteId,
        organizerId: data.organizerId,
        status: 'pending',
      },
      include: {
        organizer: { select: { name: true, logoUrl: true } },
      },
    });

    if (!invite) {
      throw new Error('Invite not found');
    }

    const expiresAt = new Date();
    expiresAt.setDate(expiresAt.getDate() + INVITE_EXPIRY_DAYS);

    // Delete and recreate to get a fresh token
    const newInvite = await db.$transaction(async (tx) => {
      await tx.organizerInvite.delete({ where: { id: invite.id } });
      return tx.organizerInvite.create({
        data: {
          email: invite.email,
          organizerId: invite.organizerId,
          invitedById: profile.id,
          expiresAt,
        },
      });
    });

    await sendInviteEmail({
      email: invite.email,
      token: newInvite.token,
      organizerName: invite.organizer.name,
      organizerLogoUrl: invite.organizer.logoUrl,
    });

    return { success: true };
  });

/**
 * Accepts an organization invite by token. Adds the authenticated
 * user as an admin of the organizer.
 */
export const acceptInvite = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid invite token');
    }
    return result.data;
  })
  .handler(async ({ data: token, context }) => {
    const { profile } = context.auth;
    const db = await prismaClient();

    const invite = await db.organizerInvite.findUnique({
      where: { token },
      include: {
        organizer: { select: { id: true, name: true, attioCompanyId: true } },
      },
    });

    if (!invite) {
      return { success: false, error: 'not_found' } as const;
    }

    if (invite.status === 'accepted') {
      return { success: false, error: 'already_accepted' } as const;
    }

    if (invite.status === 'revoked') {
      return { success: false, error: 'revoked' } as const;
    }

    if (new Date() > new Date(invite.expiresAt)) {
      return { success: false, error: 'expired' } as const;
    }

    if (invite.email.toLowerCase() !== profile.email.toLowerCase()) {
      return { success: false, error: 'email_mismatch' } as const;
    }

    // Check if the user is already an admin
    const existingAdmin = await db.organizerAdmin.findUnique({
      where: {
        profileId_organizerId: {
          profileId: profile.id,
          organizerId: invite.organizerId,
        },
      },
    });

    if (existingAdmin) {
      return {
        success: false,
        error: 'already_admin',
        organizerId: invite.organizerId,
        organizerName: invite.organizer.name,
      } as const;
    }

    await db.$transaction([
      db.organizerAdmin.create({
        data: {
          profileId: profile.id,
          organizerId: invite.organizerId,
        },
      }),
      db.organizerInvite.update({
        where: { id: invite.id },
        data: { status: 'accepted', acceptedAt: new Date() },
      }),
    ]);

    if (invite.organizer.attioCompanyId) {
      const attioPersonId = await createAttioPerson({
        companyId: invite.organizer.attioCompanyId,
        profile,
      });

      if (attioPersonId) {
        await db.profile.update({
          where: { id: profile.id },
          data: { attioPersonId },
        });
      }
    }

    return {
      success: true,
      organizerId: invite.organizerId,
      organizerName: invite.organizer.name,
    };
  });

/**
 * Fetches invite details by token without requiring authentication.
 * Used to display invite info before the user logs in or signs up.
 */
export const getInviteByToken = createServerFn({ method: 'GET' })
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid invite token');
    }
    return result.data;
  })
  .handler(async ({ data: token }) => {
    const db = await prismaClient();

    const invite = await db.organizerInvite.findUnique({
      where: { token },
      include: {
        organizer: {
          select: { id: true, name: true, logoUrl: true },
        },
      },
    });

    if (!invite) {
      return { found: false } as const;
    }

    const isExpired = new Date() > new Date(invite.expiresAt);

    return {
      found: true,
      status: invite.status,
      isExpired,
      email: invite.email,
      organizerId: invite.organizer.id,
      organizerName: invite.organizer.name,
      organizerLogoUrl: invite.organizer.logoUrl,
    } as const;
  });

/**
 * Returns the list of current admins and pending invites
 * for the specified organization.
 */
export const getOrganizerAdmins = createServerFn({ method: 'GET' })
  .middleware([authMiddleware])
  .inputValidator((data: unknown): string => {
    const result = z.string().uuid().safeParse(data);
    if (!result.success) {
      throw new Error('Invalid organizer ID format');
    }
    return result.data;
  })
  .handler(async ({ data: organizerId, context }) => {
    await requireOrganizerAccess(organizerId, context.auth);
    const db = await prismaClient();

    const [admins, invites] = await Promise.all([
      db.organizerAdmin.findMany({
        where: { organizerId },
        include: {
          profile: {
            select: {
              id: true,
              email: true,
              displayName: true,
              firstName: true,
              lastName: true,
              avatarUrl: true,
            },
          },
        },
        orderBy: { createdAt: 'asc' },
      }),
      db.organizerInvite.findMany({
        where: { organizerId, status: 'pending' },
        orderBy: { createdAt: 'desc' },
      }),
    ]);

    return {
      admins: admins.map((a) => ({
        id: a.id,
        createdAt: a.createdAt,
        profile: a.profile,
      })),
      invites: invites.map((i) => ({
        id: i.id,
        email: i.email,
        createdAt: i.createdAt,
        expiresAt: i.expiresAt,
        isExpired: new Date() > new Date(i.expiresAt),
      })),
    };
  });

/**
 * Revokes a pending invite, preventing it from being accepted.
 */
export const revokeInvite = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof RevokeInviteInput> => {
      const result = RevokeInviteInput.safeParse(data);
      if (!result.success) {
        throw new Error('Invalid input');
      }
      return result.data;
    }
  )
  .handler(async ({ data, context }) => {
    await requireOrganizerAccess(data.organizerId, context.auth);
    const db = await prismaClient();

    const result = await db.organizerInvite.updateMany({
      where: { id: data.inviteId, organizerId: data.organizerId, status: 'pending' },
      data: { status: 'revoked' },
    });

    if (result.count === 0) {
      throw new Error('Invite not found or not pending');
    }

    return { success: true };
  });

/**
 * Removes an admin from an organization. Cannot remove the last admin.
 */
export const removeAdmin = createServerFn({ method: 'POST' })
  .middleware([authMiddleware])
  .inputValidator(
    (data: unknown): z.infer<typeof RemoveAdminInput> => {
      const result = RemoveAdminInput.safeParse(data);
      if (!result.success) {
        throw new Error('Invalid input');
      }
      return result.data;
    }
  )
  .handler(async ({ data, context }) => {
    await requireOrganizerAccess(data.organizerId, context.auth);
    const db = await prismaClient();

    const adminRow = await db.organizerAdmin.findFirst({
      where: { id: data.adminId, organizerId: data.organizerId },
    });

    if (!adminRow) {
      throw new Error('Admin not found');
    }

    const adminCount = await db.organizerAdmin.count({
      where: { organizerId: data.organizerId },
    });
    if (adminCount <= 1) {
      throw new Error('Cannot remove the last admin of an organization');
    }

    await db.organizerAdmin.delete({
      where: { id: data.adminId },
    });

    return { success: true };
  });

/**
 * Sends an organization invite email via Plunk event tracking,
 * which triggers a pre-configured email template in Plunk.
 */
async function sendInviteEmail({
  email,
  token,
  organizerName,
  organizerLogoUrl,
}: {
  email: string;
  token: string;
  organizerName: string;
  organizerLogoUrl: string | null;
}) {
  const { publicKey } = await getPlunkCredentials();
  const inviteUrl = `${SITE_URL}/invite/${token}`;

  const response = await fetch('https://next-api.useplunk.com/v1/track', {
    method: 'POST',
    headers: {
      Authorization: `Bearer ${publicKey}`,
      'Content-Type': 'application/json',
    },
    body: JSON.stringify({
      event: 'organization.invite',
      email,
      subscribed: true,
      data: {
        organizerName,
        organizerLogoUrl: organizerLogoUrl || '',
        inviteUrl,
      },
    }),
  });

  if (!response.ok) {
    const errorText = await response.text();
    console.error('Failed to send invite email:', errorText);
    throw new Error(`Failed to send invite email: ${response.status}`);
  }
}
